import React, { useEffect, useState } from 'react';
import Button from './ui/Button';
import { createOrRotateDraftShare, getDraftShare, revokeDraftShare, getInstitutionSettings } from '../services/apiService';

const ShareDraftModal = ({ isOpen, onClose, currentDraftName, institutionUrlEndpoint }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [share, setShare] = useState(null);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            if (!isOpen || !currentDraftName) return;
            setError(null);
            setLoading(true);
            try {
                const meta = await getDraftShare(currentDraftName);
                if (mounted) setShare(meta);
            } catch (_) {
                if (mounted) setShare(null);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [isOpen, currentDraftName]);

    const [endpoint, setEndpoint] = useState(institutionUrlEndpoint || '');
    useEffect(() => {
        let mounted = true;
        const ensureEndpoint = async () => {
            if (endpoint) return;
            try {
                const settings = await getInstitutionSettings();
                if (mounted && settings && settings.url_endpoint) {
                    setEndpoint(settings.url_endpoint);
                }
            } catch (_) { /* ignore */ }
        };
        ensureEndpoint();
        return () => { mounted = false; };
    }, [endpoint]);

    const basePublicHost = window.location.origin.replace(/\/pi-admin.*$/, '');

    const publicUrl = share && share.slug && endpoint
        ? `${window.location.origin}/${endpoint}/d/${share.slug}`
        : '';

    const handleCreateOrRotate = async (rotate = false) => {
        setLoading(true); setError(null);
        try {
            const meta = await createOrRotateDraftShare(currentDraftName, { rotate });
            setShare(meta);
        } catch (e) {
            setError(e.message || 'Failed to create share');
        } finally { setLoading(false); }
    };

    const handleRevoke = async () => {
        setLoading(true); setError(null);
        try {
            await revokeDraftShare(currentDraftName);
            setShare(null);
        } catch (e) {
            setError(e.message || 'Failed to revoke share');
        } finally { setLoading(false); }
    };

    const handleCopy = async () => {
        if (!publicUrl) return;
        try { await navigator.clipboard.writeText(publicUrl); } catch (_) { }
    };

    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
                <div className="px-5 py-4 border-b flex items-center justify-between">
                    <div>
                        <div className="text-lg font-semibold text-gray-900">Share Draft</div>
                        <div className="text-xs text-gray-600">Draft: <span className="font-medium">{currentDraftName}</span></div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    {error && (
                        <div className="bg-red-50 text-red-700 text-sm rounded p-2 border border-red-200">{error}</div>
                    )}

                    {!share && (
                        <div className="space-y-3">
                            <p className="text-sm text-gray-700">Create a public link to let anyone view this draft (read-only).</p>
                            <Button onClick={() => handleCreateOrRotate(false)} disabled={loading} variant="primary">Create public link</Button>
                        </div>
                    )}

                    {share && (
                        <div className="space-y-3">
                            <div>
                                <div className="text-sm text-gray-700 mb-1">Public link</div>
                                <div className="flex items-center gap-2">
                                    <input className="flex-1 border rounded px-2 py-1 text-sm" value={publicUrl} readOnly />
                                    <Button onClick={handleCopy} variant="secondary">Copy</Button>
                                    <a className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded" href={publicUrl} target="_blank" rel="noreferrer">Open</a>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button onClick={() => handleCreateOrRotate(true)} disabled={loading} variant="secondary">Regenerate link</Button>
                                <Button onClick={handleRevoke} disabled={loading} variant="danger">Disable link</Button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="px-5 py-4 border-t flex justify-end">
                    <Button onClick={onClose} variant="secondary">Close</Button>
                </div>
            </div>
        </div>
    );
};

export default ShareDraftModal;


